/* eslint-disable camelcase */
import React, { MutableRefObject, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { LocationCard } from '../../locationcard/LocationCard';
import SelectComponent from '../../../operations/shifts/SelectComponent';
import { AdditionalInfo } from './AdditionalInfo';
import { updateField } from '../../../../actions/BookingRequest';
import {
    fetchClientAddressListAsync,
    fetchClientAddressList,
    fetchClientData,
    fetchPaymentMethods,
    clearPaymentMethods,
} from '../../../../actions/ClientData';
import { TRootState } from '../../../../reducers';
import { usePrevious } from '../../../../hooks';
import { jobModes } from '../../../../consts';

export type TClientData = {
    id: number;
    reference_number: string;
    email: string;
    first_name: string;
    last_name: string;
    company_name: string;
    mobile: string;
    logo: string;
    legal_name: string;
    billing_email: string;
    billing_address_1: string;
    billing_address_2: string;
    billing_city: string;
    billing_postcode: string;
    default_admin_information: string;
    pay_by_banktransfer: boolean;
    average_score: number;
    category: string;
    last_booking_date: string;
    date_joined: string;
    total_past_bookings: number;
    total_future_bookings: number;
    default_freelancer_information: string;
    signup_medium: string;
    default_consultant: number;
};

const initialState: TClientData = {
    id: null as number,
    reference_number: null as string,
    email: null,
    first_name: null,
    last_name: null,
    company_name: null,
    mobile: null,
    logo: null,
    legal_name: '',
    billing_email: null,
    billing_address_1: null,
    billing_address_2: null,
    billing_city: null,
    billing_postcode: null,
    default_admin_information: null,
    pay_by_banktransfer: false,
    average_score: null,
    category: null,
    last_booking_date: null,
    date_joined: null,
    total_past_bookings: null,
    total_future_bookings: null,
    default_freelancer_information: null,
    signup_medium: null,
    default_consultant: null,
};

type TAgentLocationCard = {
    className?: string;
    mode: string;
    freelancersInformationRef?: MutableRefObject<string>;
    natureRef?: MutableRefObject<string>;
    qualificationsRef?: MutableRefObject<string>;
    risksRef?: MutableRefObject<string>;
    facilitiesRef?: MutableRefObject<string>;
    payableRef?: MutableRefObject<string>;
    titleRef?: MutableRefObject<string>;
    freelancersInformationLabelRef?: MutableRefObject<string>;
    natureLabelRef?: MutableRefObject<string>;
    qualificationsLabelRef?: MutableRefObject<string>;
    risksLabelRef?: MutableRefObject<string>;
    facilitiesLabelRef?: MutableRefObject<string>;
    payableLabelRef?: MutableRefObject<string>;
    titleLabelRef?: MutableRefObject<string>;
    defaultNature: string;
    defaultQualifications: string;
    defaultRisks: string;
    defaultFacilities: string;
    defaultPayable: string;
};

export function AgentLocationCard({
    className,
    mode,
    freelancersInformationRef,
    natureRef,
    qualificationsRef,
    risksRef,
    facilitiesRef,
    payableRef,
    titleRef,
    freelancersInformationLabelRef,
    natureLabelRef,
    qualificationsLabelRef,
    risksLabelRef,
    facilitiesLabelRef,
    payableLabelRef,
    titleLabelRef,
    defaultNature,
    defaultQualifications,
    defaultRisks,
    defaultFacilities,
    defaultPayable,
}: TAgentLocationCard) {
    const {
        address1,
        address2,
        admin_information,
        basicInfo,
        city,
        clientAddressList,
        clientFull,
        freelancers_information,
        nature_of_business,
        required_qualifications,
        safety_risks,
        available_facilities,
        expenses_payable,
        title,
        postcode,
        user,
        defaultAgencyConsultantId,
    } = useSelector((state: TRootState) => ({
        address1: state.requestData.bookingRequest.address1,
        address2: state.requestData.bookingRequest.address2,
        admin_information: state.requestData.bookingRequest.admin_information,
        basicInfo: state.clientData.basicInfo,
        city: state.requestData.bookingRequest.city,
        clientAddressList: state.clientData.clientAddressList,
        clientFull: state.requestData.bookingRequest?.client_full,
        freelancers_information: state.requestData.bookingRequest.freelancers_information,
        nature_of_business: state.requestData.bookingRequest.nature_of_business,
        required_qualifications: state.requestData.bookingRequest.required_qualifications,
        safety_risks: state.requestData.bookingRequest.safety_risks,
        available_facilities: state.requestData.bookingRequest.available_facilities,
        expenses_payable: state.requestData.bookingRequest.expenses_payable,
        title: state.requestData.bookingRequest.title,
        default_assignment_details: state.user.agency.default_assignment_details,
        postcode: state.requestData.bookingRequest.postcode,
        user: state.user,
        defaultAgencyConsultantId: state.user.agency.default_consultant,
    }));

    const dispatch = useDispatch();

    const prevBasicInfo = usePrevious(basicInfo);
    const prevClientFull = usePrevious(clientFull);
    const prevClientAddressList = usePrevious(clientAddressList);

    const getClientData = (clientData: TClientData) => {
        dispatch(fetchClientAddressListAsync(clientData.id));
        dispatch(fetchClientData(clientData));
        dispatch(fetchPaymentMethods(clientData.id));

        if (mode === jobModes.CREATE || clientData.id !== basicInfo.id) {
            dispatch(updateField('admin_information', clientData.default_admin_information));
            dispatch(updateField('freelancers_information', clientData.default_freelancer_information));

            if (clientData.default_consultant || defaultAgencyConsultantId) {
                dispatch(updateField('consultant_splits', [{
                    agent: clientData.default_consultant || defaultAgencyConsultantId,
                    split_value: 100,
                }]));
            }
        }
    };

    const clearClientData = () => {
        dispatch(fetchClientAddressList([]));
        dispatch(fetchClientData(initialState));
        dispatch(clearPaymentMethods());
        dispatch(updateField('address1', null));
        dispatch(updateField('address2', null));
        dispatch(updateField('postcode', null));
        dispatch(updateField('city', null));
    };

    const choseClient = (clientData: TClientData) => {
        if (clientData) {
            getClientData(clientData);
        } else {
            clearClientData();
        }
    };

    useEffect(() => {
        if (mode === jobModes.EDIT || mode === jobModes.DUPLICATE) {
            if (clientFull?.id !== prevClientFull?.id) {
                choseClient(clientFull);
            }
        } else {
            // eslint-disable-next-line no-lonely-if
            if (basicInfo?.id !== prevBasicInfo?.id) {
                choseClient(basicInfo);
            }
        }
    }, [basicInfo, clientAddressList, prevBasicInfo, prevClientAddressList]);

    return (
        <div className={classNames('card agent-location-card', className)}>
            <div className="card-content">
                <p className="card-title">
                    {user.agency.features.includes('external-clients')
                        ? 'Client'
                        : 'Venue'}{' '}
                </p>
                <div className="content-container">
                    <div className="agent-location-card__client">
                        <SelectComponent
                            // @ts-ignore
                            clientId={mode !== jobModes.CREATE ? clientFull?.id : basicInfo?.id}
                            disabled={mode === jobModes.EDIT}
                            update={choseClient}
                        />
                    </div>
                    <LocationCard
                        mode={mode}
                        inputOnly={true}
                        address1={address1}
                        address2={address2}
                        postcode={postcode}
                        city={city}
                        updateField={(field: string, data: string) =>
                            dispatch(updateField(field, data))
                        }
                    />
                    <AdditionalInfo
                        updateField={(field: string, data: string) =>
                            dispatch(updateField(field, data))
                        }
                        freelancers_information={freelancers_information}
                        admin_information={admin_information}
                        nature_of_business={nature_of_business}
                        required_qualifications={required_qualifications}
                        safety_risks={safety_risks}
                        available_facilities={available_facilities}
                        expenses_payable={expenses_payable}
                        title={title}
                        default_nature_of_business={defaultNature}
                        default_required_qualifications={defaultQualifications}
                        default_safety_risks={defaultRisks}
                        default_available_facilities={defaultFacilities}
                        default_expenses_payable={defaultPayable}
                        freelancersInformationRef={freelancersInformationRef}
                        natureRef={natureRef}
                        qualificationsRef={qualificationsRef}
                        risksRef={risksRef}
                        facilitiesRef={facilitiesRef}
                        payableRef={payableRef}
                        titleRef={titleRef}
                        freelancersInformationLabelRef={freelancersInformationLabelRef}
                        natureLabelRef={natureLabelRef}
                        qualificationsLabelRef={qualificationsLabelRef}
                        risksLabelRef={risksLabelRef}
                        facilitiesLabelRef={facilitiesLabelRef}
                        payableLabelRef={payableLabelRef}
                        titleLabelRef={titleLabelRef}
                    />
                </div>
            </div>
        </div>
    );
}
